// app/(app)/inventory/products/add.tsx
import { Button, Input } from "@/components/default/forms";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { Unit } from "@/services/unitsApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearError, createProduct } from "@/store/slices/productSlice";
import { fetchUnits } from "@/store/slices/unitSlice";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

export default function AddProductScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { updating, error } = useAppSelector((state) => state.products);

  const gstOptions = [
    { label: "0%", value: "0" },
    { label: "5%", value: "5" },
    { label: "12%", value: "12" },
    { label: "18%", value: "18" },
    { label: "28%", value: "28" },
  ];

  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [price, setPrice] = useState("");
  const [packQuantity, setPackQuantity] = useState("");
  const [gstPercent, setGstPercent] = useState("");
  const [unitId, setUnitId] = useState<number | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [errors, setErrors] = useState({
    name: "",
    sku: "",
    hsnCode: "",
    price: "",
    packQuantity: "",
    gstPercent: "",
    unitId: "",
  });

  useEffect(() => {
    const loadUnits = async () => {
      const result = await dispatch(
        fetchUnits({ page: 1, limit: 100, search: "" }),
      );
      if (fetchUnits.fulfilled.match(result)) {
        setUnits(result.payload.units);
      }
    };
    loadUnits();
  }, []);

  useFocusEffect(
    useCallback(() => {
      setName("");
      setSku("");
      setHsnCode("");
      setPrice("");
      setPackQuantity("");
      setGstPercent("");
      setUnitId(null);
      setErrors({
        name: "",
        sku: "",
        hsnCode: "",
        price: "",
        packQuantity: "",
        gstPercent: "",
        unitId: "",
      });
      dispatch(clearError());
    }, []),
  );

  useEffect(() => {
    if (error) Alert.alert("Error", error);
  }, [error]);

  const validate = () => {
    let valid = true;
    const newErrors = {
      name: "",
      sku: "",
      hsnCode: "",
      price: "",
      packQuantity: "",
      gstPercent: "",
      unitId: "",
    };
    if (!name.trim()) {
      newErrors.name = "Required";
      valid = false;
    }
    if (!sku.trim()) {
      newErrors.sku = "Required";
      valid = false;
    }
    if (!hsnCode.trim()) {
      newErrors.hsnCode = "Required";
      valid = false;
    }
    const priceNum = parseFloat(price);
    if (!price) {
      newErrors.price = "Required";
      valid = false;
    } else if (isNaN(priceNum) || priceNum <= 0) {
      newErrors.price = "Must be > 0";
      valid = false;
    }
    const pack = parseInt(packQuantity);
    if (!packQuantity) {
      newErrors.packQuantity = "Required";
      valid = false;
    } else if (isNaN(pack) || pack < 1) {
      newErrors.packQuantity = "Must be ≥ 1";
      valid = false;
    }
    const gst = parseFloat(gstPercent);
    if (!gstPercent) {
      newErrors.gstPercent = "Required";
      valid = false;
    } else if (isNaN(gst) || gst < 0 || gst > 100) {
      newErrors.gstPercent = "0-100 only";
      valid = false;
    }
    if (!unitId) {
      newErrors.unitId = "Select unit";
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const result = await dispatch(
      createProduct({
        name,
        sku,
        hsnCode,
        price: parseFloat(price),
        packQuantity: parseInt(packQuantity),
        unitId: unitId!,
        gstPercent,
      }),
    );
    if (createProduct.fulfilled.match(result)) {
      Alert.alert("Success", "Product added", [
        { text: "OK", onPress: () => router.back() },
      ]);
    }
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.lg,
    },
    inputContainer: { marginBottom: spacing.lg },
  });

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.title}>Add Product</Text>
          <Input
            label="Product Name"
            value={name}
            onChangeText={setName}
            error={errors.name}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="SKU"
            value={sku}
            onChangeText={setSku}
            error={errors.sku}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="HSN Code"
            value={hsnCode}
            onChangeText={setHsnCode}
            error={errors.hsnCode}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="Price (₹)"
            value={price}
            onChangeText={setPrice}
            keyboardType="numeric"
            error={errors.price}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="Pack Quantity (per unit)"
            value={packQuantity}
            onChangeText={setPackQuantity}
            keyboardType="numeric"
            error={errors.packQuantity}
            containerStyle={styles.inputContainer}
          />
          <SearchableSelect
            label="Unit"
            placeholder="Select unit"
            options={units.map((u) => ({
              label: `${u.name} (${u.symbol})`,
              value: u.id.toString(),
            }))}
            value={unitId?.toString()}
            onValueChange={(val) => setUnitId(parseInt(val))}
            error={errors.unitId}
            containerStyle={styles.inputContainer}
          />
          <SearchableSelect
            label="GST %"
            placeholder="Select GST rate"
            options={gstOptions}
            value={gstPercent}
            onValueChange={setGstPercent}
            error={errors.gstPercent}
            containerStyle={styles.inputContainer}
          />
          <Button
            title="Save Product"
            onPress={handleSubmit}
            loading={updating}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
